/**
 * @fileoverview Dashboard widget wrapper component
 *
 * Wraps individual dashboard widgets with absolute positioning calculated
 * from grid configuration. Supports smooth animations, drag/resize in edit mode,
 * and fullscreen expansion with backdrop overlay.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useGridContext } from './DashboardGrid';

/**
 * DashboardWidget - Wrapper component for grid-positioned widgets
 *
 * @param {Object} props
 * @param {string} props.id - Unique widget identifier (must match layout config key)
 * @param {React.ReactNode} props.children - Widget content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.title - Optional widget title for edit mode
 * @param {string} props.transition - CSS transition string (default: 'all 500ms ease-in-out')
 * @param {number} props.zIndex - Z-index for layering (default: 10)
 * @param {boolean} props.isExpanded - Whether widget is expanded fullscreen
 * @param {Function} props.onExpand - Callback when widget should expand
 * @param {Function} props.onCollapse - Callback when widget should collapse
 */
function DashboardWidget({
  id,
  children,
  className = '',
  title = '',
  transition = 'all 500ms ease-in-out',
  zIndex = 10,
  isExpanded = false,
  onExpand,
  onCollapse
}) {
  const {
    isEditing,
    layout,
    getWidgetPosition,
    onWidgetMove,
    onWidgetResize,
    columns,
    rows,
    gridContainerRef
  } = useGridContext();

  const widgetRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isAnimatingExpand, setIsAnimatingExpand] = useState(false);

  // Use refs to track drag state to avoid stale closures
  const dragStartRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);

  const widgetLayout = layout[id] || { colStart: 1, colSpan: 1, rowStart: 1, rowSpan: 1 };
  const position = getWidgetPosition(id);

  // Handle expand animation
  useEffect(() => {
    if (isExpanded) {
      setIsAnimatingExpand(true);
    }
  }, [isExpanded]);

  // Handle escape key to collapse
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onCollapse) {
        onCollapse();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, onCollapse]);

  // Calculate grid cell size in pixels for drag calculations
  const getGridCellSize = useCallback(() => {
    if (!widgetRef.current?.parentElement) return { cellWidth: 0, cellHeight: 0 };
    const parent = widgetRef.current.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const cellWidth = parentRect.width / columns;
    const cellHeight = parentRect.height / rows;
    return { cellWidth, cellHeight };
  }, [columns, rows]);

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    if (!isEditing || isExpanded) return;
    e.preventDefault();
    e.stopPropagation();

    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  }, [isEditing, isExpanded]);

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;

    const { cellWidth, cellHeight } = getGridCellSize();
    if (cellWidth === 0 || cellHeight === 0) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    // Calculate how many grid cells we've moved
    const colDelta = Math.round(deltaX / cellWidth);
    const rowDelta = Math.round(deltaY / cellHeight);

    if (colDelta !== 0 || rowDelta !== 0) {
      onWidgetMove(id, colDelta, rowDelta);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [getGridCellSize, onWidgetMove, id]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    if (!isEditing || isExpanded) return;
    e.preventDefault();
    e.stopPropagation();

    isResizingRef.current = true;
    setIsResizing(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  }, [isEditing, isExpanded]);

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (!isResizingRef.current) return;

    const { cellWidth, cellHeight } = getGridCellSize();
    if (cellWidth === 0 || cellHeight === 0) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    const colDelta = Math.round(deltaX / cellWidth);
    const rowDelta = Math.round(deltaY / cellHeight);

    if (colDelta !== 0 || rowDelta !== 0) {
      onWidgetResize(id, colDelta, rowDelta);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [getGridCellSize, onWidgetResize, id]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
  }, []);

  // Setup global mouse event listeners when dragging/resizing
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      if (isDraggingRef.current) {
        handleDragMove(e);
      } else if (isResizingRef.current) {
        handleResizeMove(e);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        handleDragEnd();
      }
      if (isResizingRef.current) {
        handleResizeEnd();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleDragMove, handleResizeMove, handleDragEnd, handleResizeEnd]);

  // Handle mouse down on drag handle
  const handleDragHandleMouseDown = useCallback((e) => {
    handleDragStart(e);
  }, [handleDragStart]);

  // Handle mouse down on resize handle
  const handleResizeHandleMouseDown = useCallback((e) => {
    handleResizeStart(e);
  }, [handleResizeStart]);

  // Handle backdrop click to collapse
  const handleBackdropClick = useCallback(() => {
    if (onCollapse) {
      onCollapse();
    }
  }, [onCollapse]);

  // Widget style - animates between normal and expanded states
  const widgetStyle = isExpanded ? {
    // Expanded: fill the entire grid container
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    zIndex: 1000,
    transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default'
  } : {
    // Normal: grid-positioned
    position: 'absolute',
    left: position.left,
    top: position.top,
    width: position.width,
    height: position.height,
    zIndex: isDragging || isResizing ? 100 : zIndex,
    transition: isDragging || isResizing ? 'none' : transition,
    cursor: isEditing ? (isDragging ? 'grabbing' : 'default') : 'default'
  };

  // Render backdrop overlay via portal when expanded
  const backdropOverlay = isExpanded && gridContainerRef?.current ? createPortal(
    <div
      className="absolute inset-0 bg-black/80 backdrop-blur-sm z-[999] transition-opacity duration-400"
      style={{ opacity: isAnimatingExpand ? 1 : 0 }}
      onClick={handleBackdropClick}
    />,
    gridContainerRef.current
  ) : null;

  return (
    <>
      {backdropOverlay}
      <div
        ref={widgetRef}
        className={`dashboard-widget ${className} ${isDragging ? 'opacity-90 shadow-2xl' : ''} ${isExpanded ? 'shadow-2xl' : ''}`}
        style={widgetStyle}
      >
        {/* Close button when expanded */}
        {isExpanded && (
          <button
            onClick={onCollapse}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Close (Esc)"
          >
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
        )}
      {/* Edit mode overlay */}
      {isEditing && (
        <>
          {/* Drag handle bar at top */}
          <div
            className="widget-drag-handle absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-obn-blue/30 to-transparent rounded-t-2xl cursor-grab active:cursor-grabbing z-20 flex items-center justify-center"
            onMouseDown={handleDragHandleMouseDown}
          >
            <div className="flex space-x-1">
              <div className="w-1 h-1 rounded-full bg-white/40"></div>
              <div className="w-1 h-1 rounded-full bg-white/40"></div>
              <div className="w-1 h-1 rounded-full bg-white/40"></div>
            </div>
          </div>

          {/* Title badge */}
          {title && (
            <div className="absolute top-1 left-3 z-30 px-2 py-0.5 bg-obn-blue rounded text-xs text-white font-medium shadow">
              {title}
            </div>
          )}

          {/* Grid position indicator */}
          <div className="absolute top-1 right-8 z-30 px-2 py-0.5 bg-gray-700/90 rounded text-xs text-gray-300 font-mono shadow">
            {widgetLayout.colStart},{widgetLayout.rowStart} · {widgetLayout.colSpan}×{widgetLayout.rowSpan}
          </div>

          {/* Resize handle */}
          <div
            className="absolute bottom-1 right-1 w-6 h-6 cursor-se-resize z-30 group flex items-center justify-center"
            onMouseDown={handleResizeHandleMouseDown}
          >
            <svg
              className="w-4 h-4 text-white/40 group-hover:text-white/80 transition-colors"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
            </svg>
          </div>

          {/* Edit mode border */}
          <div className="absolute inset-0 border-2 border-dashed border-obn-lightblue/50 rounded-2xl pointer-events-none z-10" />
        </>
      )}

      {children}
    </div>
    </>
  );
}

export default DashboardWidget;
